// Mori message card subscriber.
//
// Subscribes to lifecycle events and spawns a row in user_card_instances
// whenever a content_library card has a matching trigger_event. The card's
// body is interpolated with the event payload at display time ({{variable}}).
//
// Adding a new Mori message:
//   1. Create a card in Admin → Content Library
//      • set trigger_event to the MoriEventName
//      • write body with {{variableName}} placeholders
//   2. Add an onEvent() call below that forwards to createMoriMessage
//      with the same trigger name.
// No other code changes needed.

import { supabase } from './supabase'
import { onEvent, fireEvent } from './events'

async function createMoriMessage(
  userId: string,
  triggerEvent: string,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    // Find the active template for this trigger. Intentionally .maybeSingle()
    // so "no template" is a silent no-op rather than an error.
    const { data: template } = await supabase
      .from('content_library')
      .select('id')
      .eq('trigger_event', triggerEvent)
      .eq('active', true)
      .maybeSingle()

    if (!template) return

    // Strip internal event-bus fields from the payload before persisting.
    const {
      _event: _evt,
      _category: _cat,
      ...cleanPayload
    } = payload as Record<string, unknown> & { _event?: unknown; _category?: unknown }

    const { data: inserted } = await supabase
      .from('user_card_instances')
      .insert({
        user_id: userId,
        content_library_id: (template as { id: string }).id,
        payload: cleanPayload as Record<string, string>,
      })
      .select('id')
      .single()

    if (inserted) {
      fireEvent('mori_message.created', {
        userId,
        instanceId: (inserted as { id: string }).id,
        triggerEvent,
      })
    }
  } catch (err) {
    console.error('[moriMessage] failed to create instance:', err)
  }
}

export function initMoriMessageSubscriber(): void {
  onEvent('app.first.open', (p) => {
    createMoriMessage(p.userId, 'app.first.open', p as unknown as Record<string, unknown>)
  })

  onEvent('child.birthday.today', (p) => {
    createMoriMessage(p.userId, 'child.birthday.today', p as unknown as Record<string, unknown>)
  })

  onEvent('streak.broken', (p) => {
    createMoriMessage(p.userId, 'streak.broken', p as unknown as Record<string, unknown>)
  })

  onEvent('streak.updated', (p) => {
    if (!p.isMilestone) return
    createMoriMessage(p.userId, 'streak.milestone', p as unknown as Record<string, unknown>)
  })

  onEvent('onboarding.completed', (p) => {
    createMoriMessage(p.userId, 'onboarding.completed', p as unknown as Record<string, unknown>)
  })

  onEvent('memory.anniversary', (p) => {
    createMoriMessage(p.userId, 'memory.anniversary', p as unknown as Record<string, unknown>)
  })

  onEvent('tribe.member.first.memory', (p) => {
    createMoriMessage(p.userId, 'tribe.member.first.memory', p as unknown as Record<string, unknown>)
  })

  onEvent('storage.threshold.reached', (p) => {
    createMoriMessage(p.userId, 'storage.threshold.reached', p as unknown as Record<string, unknown>)
  })

  onEvent('tribe.invite.sent', (p) => {
    createMoriMessage(p.userId, 'tribe.invite.sent', p as unknown as Record<string, unknown>)
  })

  onEvent('feedback.monthly_prompt', (p) => {
    createMoriMessage(p.userId, 'feedback.monthly_prompt', p as unknown as Record<string, unknown>)
  })

  onEvent('shop.purchased', (p) => {
    createMoriMessage(p.userId, 'shop.purchased', p as unknown as Record<string, unknown>)
  })

  onEvent('shop.voucher.redeemed', (p) => {
    createMoriMessage(p.userId, 'shop.voucher.redeemed', p as unknown as Record<string, unknown>)
  })

  onEvent('shop.code.revealed', (p) => {
    createMoriMessage(p.userId, 'shop.code.revealed', p as unknown as Record<string, unknown>)
  })

  onEvent('challenge.completed', (p) => {
    createMoriMessage(p.userId, 'challenge.completed', p as unknown as Record<string, unknown>)
  })
}
